/* eslint-disable max-lines */
import Button from '@material-ui/core/Button';
import Card from '@material-ui/core/Card';
import Link from '@material-ui/core/Link';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Typography from '@material-ui/core/Typography';
import { IStateList, ListComponent, TableCellSortable } from 'components/Abstract/List';
import Alert from 'components/Shared/Alert';
import TableWrapper from 'components/Shared/TableWrapper';
import { WithStyles } from 'decorators/withStyles';
import { IDocument } from 'interfaces/document';
import { IFile } from 'interfaces/file';
import { IPaginationParams } from 'interfaces/pagination';
import { userRoles } from 'interfaces/user';
import { IUserToken } from 'interfaces/userToken';
import React, { Fragment } from 'react';
import { combineLatest } from 'rxjs';
import RxOp from 'rxjs-operators';
import authService from 'services/auth';
import fileService from 'services/file';

import FileFormDialog from './FileFormDialog';
import FileUploadDialog from './FileUploadDialog';
import Header from './Header';
import ListItem from './ListItem';

interface IState extends IStateList<IFile> {
  current?: IFile;
  uploadModalOpen?: boolean;
  formOpened?: boolean;
  user?: IUserToken;
  filesToShow: number;
}

interface IProps {
  currentIndex: number;
  lastIndex: number;
  document: IDocument;
  classes?: any;
  onClickReorder: (document: IDocument, upOrDown: '+' | '-') => void
  startDate?: Date;
  endDate?: Date;
}

@WithStyles(theme => ({
  root: {
    flexGrow: 1,
  },
  menuButton: {
    marginRight: theme.spacing(0),
  },
  header: {
    display: 'flex',
    flexDirection: 'row',
    paddingLeft: theme.spacing(2),
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingTop: theme.spacing(2),
  },
  flexRowCentered: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  },
  firstCol: {
    width: '20%'
  },
  footer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'right',
    alignItems: 'center',
    minHeight: theme.spacing(5)
  },
  columnFile: {
    width: '350px',
  }
}))
export default class FilesList extends ListComponent<IProps, IState> {
  constructor(props: IProps) {
    super(props, 'expirationDate', 'desc');
    this.state = {
      ...this.state,
      filesToShow: 1,
    };
  }

  componentDidMount() {
    this.loadData();
  }

  loadData = (params: Partial<IPaginationParams> = {}) => {
    const { document, startDate, endDate } = this.props;
    const mergedParams = this.mergeParams(params);
    combineLatest([
      fileService.listBy({ ...mergedParams, startDate, endDate }, document.ventureId, document.entityId, document.id),
      authService.getUser(),
    ]).pipe(
      RxOp.bindComponent(this),
      RxOp.logError(),
      RxOp.filter(([items, user]) => Boolean(items || user))
    ).subscribe(([items, user]) => {
      this.setPaginatedData(items);
      this.setState({ user });
    }, error => this.setError(error));
  }

  componentDidUpdate(prevProps: IProps) {
    const { startDate, endDate } = this.props;
    if (startDate !== prevProps.startDate || endDate !== prevProps.endDate) {
      this.loadData();
    }
  }

  changeStatus = (file: IFile) => {
    const items = this.state.items.map(item => item.id === file.id ? file : item);
    this.setState({ items });
  }

  openUploadDialog = () => {
    this.setState({ uploadModalOpen: true });
  }

  closeUploadDialog = () => {
    this.setState({ uploadModalOpen: false });
  }

  uploadComplete = (file?: IFile) => {
    const { items, total } = this.state;
    if (file) {
      this.setState({
        uploadModalOpen: false,
        items: [file, ...items],
        total: total + 1
      });
    } else {
      this.loadData();
    }
  }

  handleEdit = (fileId: number) => {
    const current = this.state.items.find(({ id }) => id === fileId);
    this.setState({ formOpened: true, current });
  }

  formCallback = (file?: IFile) => {
    this.setState({ formOpened: false });
    const items = this.state.items.map(item => item.id === file.id
      ? file
      : item);
    this.setState({ items });
  }

  formCancel = () => {
    this.setState({ formOpened: false });
  }

  toggleMore = () => {
    const { items, filesToShow } = this.state;
    this.setState({
      filesToShow: filesToShow === 1 ? items.length : 1
    });
  }

  showDoubtField = () => {
    const { hyperLink, doubt, titleHyperLink, name } = this.props.document;
    const hyperLinkTreated = hyperLink && hyperLink.match(/^http[s]?:\/\//) ? hyperLink : `//${hyperLink}`;

    const message = (
      <Typography color='textPrimary'>
        {doubt}
        <>
          <br />
          <Link target="_blank" rel="noopener noreferrer" href={hyperLinkTreated}>
            {titleHyperLink}
          </Link>
        </>
      </Typography>
    );

    return Alert.show({ title: `Informação: ${name}`, message, mode: 'doubt' });
  }

  renderFooterWith = () => {
    const { classes } = this.props;
    const { loading, filesToShow, items, page } = this.state;
    const actionButton = filesToShow === 1 ? 'Mais' : 'Menos';
    return (
      <div className={classes.footer}>
        {(items.length > 1 || page !== 0) && (
          <>
            <Button disabled={loading} aria-controls='mais-arquivos' aria-haspopup='false'
              onClick={this.toggleMore}>
              {actionButton}
            </Button>
            {this.renderTablePagination()}
          </>
        )}
      </div>
    );
  }

  render() {
    const { items, loading, formOpened, uploadModalOpen, current, user, filesToShow } = this.state;
    const { currentIndex, lastIndex, document, classes, onClickReorder } = this.props;

    const canUpload = user && [
      userRoles.admin,
      userRoles.auditor,
      userRoles.holding,
      userRoles.sysAdmin,
      userRoles.operator,
      userRoles.venturer].some(role => user.roles.includes(role));

    return (
      <Fragment>
        <Card elevation={0} className={classes.root}>
          {this.renderLoader()}

          {current && (
            <FileFormDialog
              opened={formOpened || false}
              file={current}
              onComplete={this.formCallback}
              onCancel={this.formCancel}
            />
          )}

          {document && (
            <FileUploadDialog
              key={Math.random()}
              opened={uploadModalOpen || false}
              document={document}
              onComplete={this.uploadComplete}
              onCancel={this.closeUploadDialog}
            />
          )}
          <Header
            currentIndex={currentIndex}
            lastIndex={lastIndex}
            canUpload={canUpload}
            user={user}
            classes={classes}
            document={document}
            loading={loading}
            showDoubtField={this.showDoubtField}
            openUploadDialog={this.openUploadDialog}
            onClickReorder={onClickReorder}
          />

          <TableWrapper minWidth={1500}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCellSortable style={{ width: '350px' }} column='filename' {...this.sortableProps}>
                    Arquivo
                  </TableCellSortable>
                  <TableCellSortable style={{ width: '125px' }} column='emissionDate' {...this.sortableProps}>
                    Emissão
                  </TableCellSortable>
                  <TableCellSortable style={{ width: '157px' }} column='expirationDate' {...this.sortableProps}>
                    Vencimento
                  </TableCellSortable>
                  <TableCellSortable style={{ width: '108px' }} column='expirationDate' {...this.sortableProps}>
                    Vence
                  </TableCellSortable>
                  <TableCellSortable style={{ width: '172px' }} column='frequency' {...this.sortableProps}>
                    Periodicidade
                  </TableCellSortable>
                  <TableCellSortable style={{ width: '110px' }} column='status' {...this.sortableProps}>
                    Status
                  </TableCellSortable>
                  <TableCellSortable style={{ width: '124px' }} column='uploadDate' {...this.sortableProps}>
                    Upload
                  </TableCellSortable>
                  <TableCellSortable style={{ width: '138px' }} column='validationDate' {...this.sortableProps}>
                    Validação
                  </TableCellSortable>
                  <TableCellSortable style={{ width: '149px' }} column='criticality' {...this.sortableProps}>
                    Criticidade
                  </TableCellSortable>
                  <TableCell>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {this.renderEmptyAndErrorMessages(11)}
                {user && items.slice(0, filesToShow).map(file =>
                  <ListItem
                    key={file.id}
                    file={file}
                    documentFrequency={document.frequency}
                    onChangeStatus={this.changeStatus}
                    onDeleteComplete={this.loadData}
                    onEdit={this.handleEdit}
                    user={user}
                  />
                )}
              </TableBody>
            </Table>
          </TableWrapper>
          {this.renderFooterWith()}

        </Card>
      </Fragment >
    );
  }
}